import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import TomSelect from "tom-select"
import { allProjectsStore } from "../../../stores/all_projects_store"

/**
 * ProjectSelectController
 *
 * A multi-select dropdown for filtering by Project, rendered with TomSelect.
 * Used primarily in the reports filter bar to scope reports by project.
 *
 * Options come from allProjectsStore. The store is filled by the projects supplier
 * (suppliers--projects--projects-supplier), which paginates /api/projects/list. On
 * Standard Reports the supplier forwards the page's organization-scope token, so the
 * filter lists every project in the organization.
 *
 * Connects to data-controller="selects--projects--project-select"
 */
export default class extends Controller {
    static stores = [
        allProjectsStore
    ]

    static values = {
        multiple: { type: Boolean, default: true }
    }

    connect() {
        useStore(this)

        // allProjectsStore is a module-level singleton, so under Turbo Drive it can still hold
        // another page's project list (e.g. /projects/manage or the users page). Clear it before
        // rendering. This synchronous reset cannot wipe this page's data: the supplier only
        // writes the store after its awaited fetch resolves, which happens in a later task than
        // this connect() pass.
        this.setAllProjectsStoreValue([])

        this._initTomSelect()
        this._renderProjects(this.allProjectsStoreValue)
    }

    disconnect() {
        if (this.tomSelect) {
            this.tomSelect.destroy()
            this.tomSelect = null
        }
    }

    onAllProjectsStoreUpdate(projects) {
        this._renderProjects(projects)
    }

    _initTomSelect() {
        this.tomSelect = new TomSelect(this.element, {
            valueField: 'id',
            labelField: 'name',
            searchField: ['name'],
            maxItems: this.multipleValue ? null : 1,
            placeholder: 'Filter by project...',
            plugins: this.multipleValue ? ['remove_button'] : [],
            options: [],
            onChange: () => {
                this._dispatchChange()
            }
        })
    }

    /**
     * Replace the dropdown options with the given projects. clearOptions() keeps any
     * currently selected options, and addOption() ignores ids that already exist, so the
     * list never holds duplicates. Rendering does not dispatch projectFilterChanged.
     */
    _renderProjects(projectsValue) {
        // useStore fires the update callback before TomSelect exists
        if (!this.tomSelect) return

        const projects = Array.isArray(projectsValue) ? projectsValue : []

        this.tomSelect.clearOptions()
        projects.forEach(project => {
            if (!project || !project.id) return
            this.tomSelect.addOption({ id: project.id, name: project.name })
        })
        this.tomSelect.refreshOptions(false)
    }

    _dispatchChange() {
        const selectedIds = this.tomSelect.getValue()

        // Dispatch custom event for other controllers to listen to
        const event = new CustomEvent('projectFilterChanged', {
            detail: { projectIds: Array.isArray(selectedIds) ? selectedIds : [selectedIds].filter(Boolean) },
            bubbles: true
        })
        this.element.dispatchEvent(event)
    }

    // Allow external controllers to get the current selection
    getSelectedProjectIds() {
        const value = this.tomSelect?.getValue()
        if (!value) return []
        return Array.isArray(value) ? value : [value].filter(Boolean)
    }
}
